/**
 * Meta (Facebook) pixel, for the ad landing page only.
 *
 * The id comes from VITE_META_PIXEL_ID at build time. Blank or unset means no
 * pixel at all: no script tag, no fbq global, no console noise. That is the
 * state the site ships in until the owner supplies an id.
 *
 * Loaded on demand by the page that wants it rather than from index.html, so
 * no other page on the site carries Meta's script.
 */

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[][];
  push: Fbq;
  loaded: boolean;
  version: string;
};

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

const SCRIPT_SRC = "https://connect.facebook.net/en_US/fbevents.js";

export function metaPixelId(): string | null {
  const raw = (import.meta.env.VITE_META_PIXEL_ID as string | undefined)?.trim();
  return raw ? raw : null;
}

const initialised = new Set<string>();

/**
 * Meta's standard bootstrap, typed: a queueing fbq stub that the real script
 * drains once it loads. Idempotent per id. Returns false when there is no id,
 * so callers can skip tracking without checking twice.
 */
export function initMetaPixel(id: string | null = metaPixelId()): boolean {
  if (!id) return false;
  if (initialised.has(id)) return true;

  if (!window.fbq) {
    const fbq = function (...args: unknown[]) {
      if (fbq.callMethod) fbq.callMethod(...args);
      else fbq.queue.push(args);
    } as Fbq;
    fbq.queue = [];
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = "2.0";
    window.fbq = fbq;
    if (!window._fbq) window._fbq = fbq;

    const script = document.createElement("script");
    script.async = true;
    script.src = SCRIPT_SRC;
    document.head.appendChild(script);
  }

  window.fbq("init", id);
  initialised.add(id);
  return true;
}

/** Fires a standard event if the pixel is loaded; a no-op otherwise. */
export function trackMetaEvent(event: "PageView" | "Lead"): void {
  window.fbq?.("track", event);
}
